import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import api from '../services/api';

// Types
export interface PaymentMethod {
  id: string;
  name: string;
  type: string;
  description: string;
  icon: string;
  fees: string;
  securityLevel?: string;
  dialCode?: string;
  limits?: {
    min: number;
    max: number;
    daily: number;
  };
  requiresPhone?: boolean;
  security?: string[];
  processingTime?: string;
  framework?: string;
  supportedChannels?: string[];
  phoneValidation?: string;
  apiStandard?: string;
  supportedCurrencies?: string[];
}

export interface PaymentRequest {
  amount: number;
  currency: string;
  paymentMethod: string;
  bookingId: string;
  userId: string;
  description?: string;
  customerInfo?: {
    email: string;
    name: string;
    phone?: string;
  };
  momoDetails?: {
    phoneNumber: string;
    network: 'MTN' | 'VODAFONE' | 'AIRTELTIGO' | 'TELECEL';
  };
  bankDetails?: {
    accountNumber: string;
    bankCode: string;
    accountName: string;
  };
  stripePaymentMethodId?: string;
}

export interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  paymentMethod: string;
  description?: string;
  createdAt: string;
  updatedAt?: string;
  paidAt?: string | null;
  paymentType?: string;
  booking: {
    id: string | null;
    property: {
      id: string;
      title: string;
      address: string;
      images: string[];
    };
  } | null;
}

interface PaymentContextType {
  stripe: Stripe | null;
  paymentMethods: PaymentMethod[];
  selectedPaymentMethod: PaymentMethod | null;
  isLoading: boolean;
  error: string | null;
  
  // Methods
  setSelectedPaymentMethod: (method: PaymentMethod) => void;
  createPayment: (request: PaymentRequest) => Promise<any>;
  getPaymentStatus: (paymentId: string) => Promise<Payment>;
  getUserPayments: (userId: string) => Promise<Payment[]>;
  clearError: () => void;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

// Stripe publishable key (using test key for development)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51234567890abcdefghijklmnopqrstuvwxyz123456789');

export const PaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize Stripe
  useEffect(() => {
    const initializeStripe = async () => {
      const stripeInstance = await stripePromise;
      setStripe(stripeInstance);
    };
    initializeStripe();
  }, []);

  // Fetch available payment methods
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await api.get('/payments/methods');
        const data = response.data;
        if (data) {
          console.log('Payment methods response:', data);
          setPaymentMethods(data.data || data);
        } else {
          // Fallback payment methods if API fails
          const fallbackMethods = [
            {
              id: 'mtn_momo',
              name: 'MTN Mobile Money',
              type: 'MTN_MOMO',
              description: 'Pay with your MTN MoMo wallet (*170#)',
              icon: '📱',
              fees: '1% (min GH₵1)',
              security: ['PIN Verification', 'SMS OTP', 'End-to-End Encryption'],
              processingTime: '1-5 minutes',
              securityLevel: 'HIGH',
              dialCode: '*170#',
              limits: { min: 1, max: 5000, daily: 10000 }
            },
            {
              id: 'vodafone_cash',
              name: 'Vodafone Cash',
              type: 'VODAFONE_CASH',
              description: 'Pay with your Vodafone Cash wallet (*110#)',
              icon: '📱',
              fees: '1% (min GH₵1)',
              security: ['PIN Verification', 'HMAC SHA-256', 'SSL/TLS 1.3'],
              processingTime: '1-5 minutes',
              securityLevel: 'HIGH',
              dialCode: '*110#',
              limits: { min: 1, max: 3000, daily: 8000 }
            },
            {
              id: 'telecel_cash',
              name: 'Telecel Cash',
              type: 'TELECEL_CASH',
              description: 'Pay with your Telecel Cash wallet (*111#)',
              icon: '📱',
              fees: '1% (min GH₵1)',
              security: ['PIN Verification', 'HMAC Authentication', 'Nonce Protection'],
              processingTime: '1-5 minutes',
              securityLevel: 'HIGH',
              dialCode: '*111#',
              limits: { min: 1, max: 2000, daily: 5000 }
            },
            {
              id: 'airteltigo_money',
              name: 'AirtelTigo Money',
              type: 'AIRTELTIGO_MONEY',
              description: 'Pay with your AirtelTigo Money wallet (*110#)',
              icon: '📱',
              fees: '1% (min GH₵1)',
              security: ['PIN Verification', 'OAuth 2.0', 'JWT Tokens'],
              processingTime: '1-5 minutes',
              securityLevel: 'HIGH',
              dialCode: '*110#',
              limits: { min: 1, max: 4000, daily: 12000 }
            },
            {
              id: 'bank_transfer',
              name: 'Bank Transfer',
              type: 'BANK_TRANSFER',
              description: 'Direct bank transfer to our account',
              icon: '🏦',
              fees: 'Bank charges apply',
              security: ['Bank Grade Security', '2FA', 'Account Verification'],
              processingTime: '1-24 hours',
              securityLevel: 'HIGH',
              limits: { min: 1, max: 100000, daily: 500000 }
            },
            {
              id: 'visa_card',
              name: 'VISA Card',
              type: 'VISA',
              description: 'Pay securely with your VISA credit or debit card',
              icon: '💳',
              fees: '2.9% + GH₵1.50',
              security: ['SSL Encryption', '3D Secure', 'PCI Compliance'],
              processingTime: 'Instant',
              securityLevel: 'HIGH',
              limits: { min: 1, max: 50000, daily: 200000 }
            },
            {
              id: 'mastercard',
              name: 'Mastercard',
              type: 'MASTERCARD',
              description: 'Pay securely with your Mastercard credit or debit card',
              icon: '💳',
              fees: '2.9% + GH₵1.50',
              security: ['SSL Encryption', '3D Secure', 'PCI Compliance'],
              processingTime: 'Instant',
              securityLevel: 'HIGH',
              limits: { min: 1, max: 50000, daily: 200000 }
            },
            {
              id: 'amex',
              name: 'American Express',
              type: 'AMEX',
              description: 'Pay with your American Express card',
              icon: '💳',
              fees: '3.4% + GH₵1.50',
              security: ['SSL Encryption', '3D Secure', 'PCI Compliance'],
              processingTime: 'Instant',
              securityLevel: 'HIGH',
              limits: { min: 1, max: 50000, daily: 200000 }
            },
            {
              id: 'paystack_card',
              name: 'Paystack Card Payment',
              type: 'PAYSTACK_CARD',
              description: 'Pay with Visa, Mastercard, or other cards via Paystack',
              icon: '💳',
              fees: '1.5% + GH₵0.50',
              security: ['256-bit SSL', 'PCI DSS Level 1', 'Fraud Detection', '3D Secure 2.0'],
              processingTime: 'Instant',
              securityLevel: 'HIGH',
              limits: { min: 1, max: 100000, daily: 500000 },
              supportedChannels: ['Card', 'Bank Transfer'],
              supportedCurrencies: ['GHS']
            },
            {
              id: 'paystack_bank',
              name: 'Paystack Bank Transfer',
              type: 'PAYSTACK_BANK',
              description: 'Direct bank transfer via Paystack',
              icon: '🏦',
              fees: '1% (min GH₵1)',
              security: ['Bank-level Encryption', 'Multi-factor Authentication', 'Fraud Detection'],
              processingTime: '1-5 minutes',
              securityLevel: 'HIGH',
              limits: { min: 10, max: 50000, daily: 200000 },
              supportedChannels: ['Online Banking', 'Mobile Banking'],
              supportedCurrencies: ['GHS']
            },
            {
              id: 'paystack_ussd',
              name: 'Paystack USSD',
              type: 'PAYSTACK_USSD',
              description: 'Pay via USSD code using Paystack',
              icon: '📱',
              fees: '1% (min GH₵1)',
              security: ['USSD Encryption', 'PIN Verification', 'Fraud Detection'],
              processingTime: '1-3 minutes',
              securityLevel: 'HIGH',
              limits: { min: 1, max: 10000, daily: 50000 },
              supportedChannels: ['USSD'],
              dialCode: '*920*',
              supportedCurrencies: ['GHS']
            }
          ];
          setPaymentMethods(fallbackMethods);
        }
      } catch (error) {
        console.error('Failed to fetch payment methods:', error);
        // Use fallback methods if API is unreachable
        const fallbackMethods = [
          {
            id: 'mtn_momo',
            name: 'MTN Mobile Money',
            type: 'MTN_MOMO',
            description: 'Pay with your MTN MoMo wallet (*170#)',
            icon: '📱',
            fees: '1% (min GH₵1)',
            security: ['PIN Verification', 'SMS OTP', 'End-to-End Encryption'],
            processingTime: '1-5 minutes',
            securityLevel: 'HIGH',
            dialCode: '*170#',
            limits: { min: 1, max: 5000, daily: 10000 }
          },
          {
            id: 'vodafone_cash',
            name: 'Vodafone Cash',
            type: 'VODAFONE_CASH',
            description: 'Pay with your Vodafone Cash wallet (*110#)',
            icon: '📱',
            fees: '1% (min GH₵1)',
            security: ['PIN Verification', 'HMAC SHA-256', 'SSL/TLS 1.3'],
            processingTime: '1-5 minutes',
            securityLevel: 'HIGH',
            dialCode: '*110#',
            limits: { min: 1, max: 3000, daily: 8000 }
          },
          {
            id: 'telecel_cash',
            name: 'Telecel Cash',
            type: 'TELECEL_CASH',
            description: 'Pay with your Telecel Cash wallet (*111#)',
            icon: '📱',
            fees: '1% (min GH₵1)',
            security: ['PIN Verification', 'HMAC Authentication', 'Nonce Protection'],
            processingTime: '1-5 minutes',
            securityLevel: 'HIGH',
            dialCode: '*111#',
            limits: { min: 1, max: 2000, daily: 5000 }
          },
          {
            id: 'airteltigo_money',
            name: 'AirtelTigo Money',
            type: 'AIRTELTIGO_MONEY',
            description: 'Pay with your AirtelTigo Money wallet (*110#)',
            icon: '📱',
            fees: '1% (min GH₵1)',
            security: ['PIN Verification', 'OAuth 2.0', 'JWT Tokens'],
            processingTime: '1-5 minutes',
            securityLevel: 'HIGH',
            dialCode: '*110#',
            limits: { min: 1, max: 4000, daily: 12000 }
          },
          {
            id: 'bank_transfer',
            name: 'Bank Transfer',
            type: 'BANK_TRANSFER',
            description: 'Direct bank transfer to our account',
            icon: '🏦',
            fees: 'Bank charges apply',
            security: ['Bank Grade Security', '2FA', 'Account Verification'],
            processingTime: '1-24 hours',
            securityLevel: 'HIGH',
            limits: { min: 1, max: 100000, daily: 500000 }
          },
          {
            id: 'visa_card',
            name: 'VISA Card',
            type: 'VISA',
            description: 'Pay securely with your VISA credit or debit card',
            icon: '💳',
            fees: '2.9% + GH₵1.50',
            security: ['SSL Encryption', '3D Secure', 'PCI Compliance'],
            processingTime: 'Instant',
            securityLevel: 'HIGH',
            limits: { min: 1, max: 50000, daily: 200000 }
          },
          {
            id: 'mastercard',
            name: 'Mastercard',
            type: 'MASTERCARD',
            description: 'Pay securely with your Mastercard credit or debit card',
            icon: '💳',
            fees: '2.9% + GH₵1.50',
            security: ['SSL Encryption', '3D Secure', 'PCI Compliance'],
            processingTime: 'Instant',
            securityLevel: 'HIGH',
            limits: { min: 1, max: 50000, daily: 200000 }
          },
          {
            id: 'paystack_card',
            name: 'Paystack Card Payment',
            type: 'PAYSTACK_CARD',
            description: 'Pay with Visa, Mastercard, or other cards via Paystack',
            icon: '💳',
            fees: '1.5% + GH₵0.50',
            security: ['256-bit SSL', 'PCI DSS Level 1', 'Fraud Detection', '3D Secure 2.0'],
            processingTime: 'Instant',
            securityLevel: 'HIGH',
            limits: { min: 1, max: 100000, daily: 500000 },
            supportedChannels: ['Card', 'Bank Transfer'],
            supportedCurrencies: ['GHS']
          },
          {
            id: 'paystack_bank',
            name: 'Paystack Bank Transfer',
            type: 'PAYSTACK_BANK',
            description: 'Direct bank transfer via Paystack',
            icon: '🏦',
            fees: '1% (min GH₵1)',
            security: ['Bank-level Encryption', 'Multi-factor Authentication', 'Fraud Detection'],
            processingTime: '1-5 minutes',
            securityLevel: 'HIGH',
            limits: { min: 10, max: 50000, daily: 200000 },
            supportedChannels: ['Online Banking', 'Mobile Banking'],
            supportedCurrencies: ['GHS']
          },
          {
            id: 'paystack_ussd',
            name: 'Paystack USSD',
            type: 'PAYSTACK_USSD',
            description: 'Pay via USSD code using Paystack',
            icon: '📱',
            fees: '1% (min GH₵1)',
            security: ['USSD Encryption', 'PIN Verification', 'Fraud Detection'],
            processingTime: '1-3 minutes',
            securityLevel: 'HIGH',
            limits: { min: 1, max: 10000, daily: 50000 },
            supportedChannels: ['USSD'],
            dialCode: '*920*',
            supportedCurrencies: ['GHS']
          }
        ];
        setPaymentMethods(fallbackMethods);
      }
    };

    fetchPaymentMethods();
  }, []);

  const createPayment = async (request: PaymentRequest) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.post('/payments/create', request);
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Payment creation failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getPaymentStatus = async (paymentId: string): Promise<Payment> => {
    try {
      setIsLoading(true);
      const response = await api.get(`/payments/${paymentId}/status`);
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to fetch payment status';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getUserPayments = async (userId: string): Promise<Payment[]> => {
    try {
      setIsLoading(true);
      const response = await api.get(`/payments/user/${userId}`);
      return response.data.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to fetch user payments';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: PaymentContextType = {
    stripe,
    paymentMethods,
    selectedPaymentMethod,
    isLoading,
    error,
    setSelectedPaymentMethod,
    createPayment,
    getPaymentStatus,
    getUserPayments,
    clearError,
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
}; 